/**
 * Listener middleware for handling post-migration layout application
 * Ensures that users migrating from v1 to v2 get the alpha template applied
 */

import { REHYDRATE } from 'redux-persist';

import { applyTemplate } from '../slices';

import {
  markMigrationNotificationShown,
  shouldShowMigrationNotification,
} from './migrations';

import type { AppStartListening } from './store';

/**
 * Register migration listeners
 * This applies the alpha template after rehydration for users migrating from v1 to v2
 */
export const registerMigrationListeners = (
  startAppListening: AppStartListening,
) => {
  // Listen for persist rehydration completion
  startAppListening({
    type: REHYDRATE,
    effect: async (_action, listenerApi) => {
      // Check if this user needs migration (v1 -> v2)
      // This check must happen BEFORE marking notification as shown
      const needsMigration = shouldShowMigrationNotification();

      if (needsMigration) {
        const state = listenerApi.getState();

        // Find alpha template in the store
        const alphaTemplate = Object.values(state.template.templates.entities)
          .filter((t): t is NonNullable<typeof t> => t !== undefined)
          .find(t => t.name === 'Alpha' && !t.isCustom);

        if (alphaTemplate) {
          // Apply alpha template to replace user's old layout
          await listenerApi.dispatch(applyTemplate(alphaTemplate.id) as never);
        }

        // Mark migration as complete to prevent re-applying on subsequent reloads
        // This ensures the alpha template is applied ONCE for v1→v2 migration
        markMigrationNotificationShown();
      }
    },
  });
};
